import { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DatePicker, { registerLocale } from "react-datepicker";
import toast, { Toaster } from "react-hot-toast";

import ru from "date-fns/locale/ru";
import moment from "moment";

import clsx from "clsx";

import {
  actionGetZipUnloadingsFilters,
  actionGetZipUnloadings,
  actionDeleteZipUnloadings,
} from "../../../redux/action/unloadings/actionZipUnloadings";
import {
  formatUnloadingsDate,
  formatUnloadingsTime,
} from "../../../utils/formatDate";
import { generateStyles } from "../../../utils/generateToastStyles";
import { createMultiOptions } from "../../../utils/createOptions";
import {
  reducerZipUnloadingsInputValues,
  reducerZipUploadingStatus,
} from "../../../redux/reducer/unloadings/reducers/zipUnloadings";
import { AppDispatch } from "../../../redux/reducer/store";

import LimitSelect from "../../custom/limitSelect/limitSelect";
import Pagination from "../../pagination/pagination";
import Select from "../../custom/select/select";
import ToastPopup from "../../custom/toastPopup/toastPopup";
import Loader from "../../custom/loader/loader";

import RefreshIcon from "../../../assets/icons/refresh.svg";

import "../table.scss";
import { IUserOptions } from "../../../types/interfaces";

interface IZipUnloadings {
  date: string;
  employee?: string;
  order: { id: number };
  status: { [key: string]: string };
  time: string;
  type: string;
  customer?: string;
  url?: string;
}

const IS_VISIBLE_SIDEBAR = process.env.REACT_APP_IS_VISIBLE_SIDEBAR;

const statusesOptions = [
  { value: "", label: "Все" },
  { value: "Ordered", label: "В очереди" },
  { value: "Error", label: "Ошибка" },
  { value: "Done", label: "Сформирован" },
  { value: "Deleted", label: "Удален" },
  { value: "Empty", label: "Фото не найдено" },
];

const timeOptions = [
  { value: "", label: "Все" },
  { value: "1", label: "Больше минуты" },
  { value: "10", label: "Больше 10 минут" },
];

const TableUnloadingZip: FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const {
    zipUnloadingsInputValues,
    zipUnloadingsStatus,
    zipUnloadingsCount,
    zipUnloadings,
    filters,
    limit,
    page,
  } = useSelector((state: any) => state.unloadings.zipUnloadings);
  const { isOpenSidebar } = useSelector(
    (state: IUserOptions) => state.user.user
  );
  const { settings } = useSelector((state: any) => state.user.user.data);

  const [selectedDate, setSelectedDate] = useState<Date | null>(
    zipUnloadingsInputValues.date && new Date(zipUnloadingsInputValues.date)
  );
  const [selectedStatusOption, setSelectedStatusOption] = useState<string>("");
  const [isPending, setIsPending] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [startPage, setStartPage] = useState<number>(1);

  const actualLimit =
    JSON.parse(localStorage.getItem("pageLimit") as string) || limit;

  const requestStatusOptions = [
    {
      status: 1,
      successMessage: `Кол-во дней хранения записей: ${settings.autoDeleteOrdersInDaysZip}.`,
    },
    {
      status: 0,
      errorMessage: "Ошибка на стороне сервера.",
    },
  ];

  useEffect(() => {
    setIsPending(true);
    dispatch(actionGetZipUnloadings(actualLimit, page)).then(() => {
      setIsPending(false);
    });
    dispatch(actionGetZipUnloadingsFilters());
    registerLocale("ru", ru);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectChange = (
    event: { value: string; label: string },
    name: string
  ) => {
    const value = event.value;
    if (value === "") {
      setSelectedStatusOption("all");
      const updatedInputValues = { ...zipUnloadingsInputValues };
      delete updatedInputValues[name];

      dispatch(reducerZipUnloadingsInputValues(updatedInputValues));
    } else {
      dispatch(
        reducerZipUnloadingsInputValues({
          ...zipUnloadingsInputValues,
          [name]: name === "moreThanMin" ? parseFloat(value) : value,
        })
      );
    }

    setIsPending(true);
    dispatch(actionGetZipUnloadings(limit, page)).then(() => {
      setIsPending(false);
    });
    resetPages();
  };

  const handleDatePickerChange = (date: Date) => {
    setSelectedDate(date);

    const formattedDate = moment(date).format("YYYY-MM-DD");

    dispatch(
      reducerZipUnloadingsInputValues({
        ...zipUnloadingsInputValues,
        date: formattedDate,
      })
    );

    setIsPending(true);
    dispatch(actionGetZipUnloadings(limit, page)).then(() => {
      setIsPending(false);
    });
  };

  const handleDateKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    event.preventDefault();
  };

  const handleClearDate = () => {
    if (selectedDate) {
      setSelectedDate(null);

      dispatch(
        reducerZipUnloadingsInputValues({
          ...zipUnloadingsInputValues,
          date: "",
        })
      );

      setIsPending(true);
      dispatch(actionGetZipUnloadings(limit, page)).then(() => {
        setIsPending(false);
      });
    }
  };

  const resetPages = () => {
    setCurrentPage(1);
    setStartPage(1);
  };

  const handleChange = (newLimit: number, newPage: number) => {
    setIsPending(true);
    dispatch(actionGetZipUnloadings(newLimit, newPage)).then(() => {
      setIsPending(false);
    });
    localStorage.setItem("pageLimit", JSON.stringify(newLimit));
  };

  const handleChangeLimits = (option: { value: number }) => {
    handleChange(option.value, 1);
    resetPages();
  };

  const handleClickRefreshBtn = () => {
    setIsPending(true);
    dispatch(actionGetZipUnloadings(limit, page)).then(() =>
      setIsPending(false)
    );
  };

  useEffect(() => {
    if (zipUnloadingsStatus) {
      requestStatusOptions.forEach((option: any) => {
        if (zipUnloadingsStatus === option.status) {
          toast.dismiss();

          toast(
            (t) => (
              <ToastPopup
                title={
                  option.successMessage
                    ? option.successMessage
                    : option.errorMessage
                }
                isSuccess={option.successMessage ? true : false}
                toast={toast}
                t={t}
              />
            ),
            generateStyles(option.successMessage ? true : false)
          );
        }
      });

      setTimeout(() => {
        dispatch(reducerZipUploadingStatus(null));
      }, 5000);
    }
    // eslint-disable-next-line
  }, [settings, zipUnloadingsStatus]);

  const handleClickDeleteBtn = (id: number) => {
    setIsPending(true);
    dispatch(actionDeleteZipUnloadings(id)).then(() => {
      dispatch(actionGetZipUnloadings(limit, page)).then(() => {
        setIsPending(false);
      });
    });
  };

  useEffect(() => {
    dispatch(reducerZipUploadingStatus(1));
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <div className="loader__wrapper">
        {(!zipUnloadings || isPending) && <Loader />}
      </div>
      <div
        className={clsx(
          JSON.parse(IS_VISIBLE_SIDEBAR as string) &&
            isOpenSidebar &&
            "active__table",
          !isOpenSidebar && "closed__table",
          "table"
        )}
      >
        <Toaster position="top-center" toastOptions={{ duration: 5000 }} />
        <div className="filters__container__mobile">
          <div className="filters__item__mobile">
            <div className="filter__data_picker filter__data_picker_excel">
              <DatePicker
                onChange={handleDatePickerChange}
                onKeyDown={handleDateKeyDown}
                selected={selectedDate}
                dateFormat="dd.MM.yyyy"
                placeholderText="Дата"
                autoComplete="off"
                locale="ru"
                name="date"
              />
              {selectedDate && (
                <div className="clear__icon" onClick={handleClearDate} />
              )}
            </div>
          </div>
          <div className="filters__item__mobile">
            <div className="filter__block">
              <Select
                propsChange={(event: { value: string; label: string }) =>
                  selectChange(event, "customer")
                }
                isShowPlaceholder={
                  (zipUnloadingsInputValues?.customer &&
                    !!Object.keys(zipUnloadingsInputValues?.customer).length) ||
                  zipUnloadingsInputValues?.customer === undefined
                }
                options={filters && createMultiOptions(filters[0]?.customers)}
                className="users__filter"
                placeholder="Клиент"
              />
            </div>
          </div>
          <div className="filters__item__mobile">
            <div className="filter__block">
              <Select
                defaultValue={
                  zipUnloadingsInputValues.status &&
                  statusesOptions.find(
                    (option: { value: string; label: string }) =>
                      option.value === zipUnloadingsInputValues.status
                  )
                }
                propsChange={(event: { value: string; label: string }) =>
                  selectChange(event, "status")
                }
                isShowPlaceholder={
                  !!zipUnloadingsInputValues.status ||
                  selectedStatusOption === "all"
                }
                options={statusesOptions}
                className="users__filter"
                placeholder="Статус"
              />
            </div>
          </div>
          <div className="filters__item__mobile">
            <div className="filter__block">
              <Select
                defaultValue={
                  zipUnloadingsInputValues.moreThanMin &&
                  timeOptions.find(
                    (option: { value: string; label: string }) =>
                      parseFloat(option.value) ===
                      zipUnloadingsInputValues.moreThanMin
                  )
                }
                propsChange={(event: { value: string; label: string }) => {
                  selectChange(event, "moreThanMin");
                }}
                isShowPlaceholder={
                  !!zipUnloadingsInputValues.moreThanMin ||
                  zipUnloadingsInputValues.moreThanMin === undefined
                }
                placeholder="Время выполнения"
                className="users__filter"
                options={timeOptions}
              />
            </div>
          </div>
          <div className="filters__item__mobile">
            <div className="refresh__btn" onClick={handleClickRefreshBtn}>
              <img className="refresh__icon" src={RefreshIcon} alt="refresh" />
            </div>
          </div>
        </div>
        <div className="table__wrapper">
          <table>
            <thead className="table__header__filters">
              <tr className="unloadings__filter_tr">
                <th className="reports__number_th">№</th>
                <th>
                  <div className="filter__data_picker filter__data_picker_excel">
                    <DatePicker
                      onChange={handleDatePickerChange}
                      onKeyDown={handleDateKeyDown}
                      selected={selectedDate}
                      dateFormat="dd.MM.yyyy"
                      placeholderText="Дата"
                      autoComplete="off"
                      locale="ru"
                      name="date"
                    />
                    {selectedDate && (
                      <div className="clear__icon" onClick={handleClearDate} />
                    )}
                  </div>
                </th>
                <th>
                  <div className="filter__block">
                    <Select
                      propsChange={(event: { value: string; label: string }) =>
                        selectChange(event, "customer")
                      }
                      isShowPlaceholder={
                        (zipUnloadingsInputValues?.customer &&
                          !!Object.keys(zipUnloadingsInputValues?.customer)
                            .length) ||
                        zipUnloadingsInputValues?.customer === undefined
                      }
                      options={
                        filters && createMultiOptions(filters[0]?.customers)
                      }
                      className="users__filter"
                      placeholder="Клиент"
                    />
                  </div>
                </th>
                <th>
                  <div className="filter__block">
                    <Select
                      defaultValue={
                        zipUnloadingsInputValues.status &&
                        statusesOptions.find(
                          (option: { value: string; label: string }) =>
                            option.value === zipUnloadingsInputValues.status
                        )
                      }
                      propsChange={(event: { value: string; label: string }) =>
                        selectChange(event, "status")
                      }
                      isShowPlaceholder={
                        !!zipUnloadingsInputValues.status ||
                        selectedStatusOption === "all"
                      }
                      options={statusesOptions}
                      className="users__filter"
                      placeholder="Статус"
                    />
                  </div>
                </th>
                <th>
                  <div className="filter__block">
                    <Select
                      defaultValue={
                        zipUnloadingsInputValues.moreThanMin &&
                        timeOptions.find(
                          (option: { value: string; label: string }) =>
                            parseFloat(option.value) ===
                            zipUnloadingsInputValues.moreThanMin
                        )
                      }
                      propsChange={(event: {
                        value: string;
                        label: string;
                      }) => {
                        selectChange(event, "moreThanMin");
                      }}
                      isShowPlaceholder={
                        !!zipUnloadingsInputValues.moreThanMin ||
                        zipUnloadingsInputValues.moreThanMin === undefined
                      }
                      placeholder="Время выполнения"
                      className="users__filter"
                      options={timeOptions}
                    />
                  </div>
                </th>
                <th className="unloadings__link_cell">Ссылка</th>
                <th>
                  <div className="refresh__btn" onClick={handleClickRefreshBtn}>
                    <img
                      className="refresh__icon"
                      src={RefreshIcon}
                      alt="refresh"
                    />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {zipUnloadings &&
                zipUnloadings.map((elem: IZipUnloadings, index: number) => (
                  <tr key={index}>
                    <td>{elem?.order?.id}</td>
                    <td>{formatUnloadingsDate(elem?.date)}</td>
                    <td>{elem?.customer}</td>
                    <td>{Object.values(elem.status)}</td>
                    <td>{formatUnloadingsTime(elem.time)}</td>
                    <td>
                      {Object.keys(elem.status).toString() === "Done" && (
                        <a href={elem?.url} download className="report__link">
                          Скачать
                        </a>
                      )}
                      {Object.keys(elem.status).toString() === "Error" && (
                        <span
                          className="report__link"
                          onClick={() =>
                            dispatch(actionGetZipUnloadings(limit, page))
                          }
                        >
                          Повторить
                        </span>
                      )}
                      {elem.status &&
                        Object.keys(elem.status).toString() === "Ordered" && (
                          <span
                            className="report__link"
                            onClick={() => handleClickDeleteBtn(elem.order.id)}
                          >
                            Отмена
                          </span>
                        )}
                    </td>
                    <td></td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <div className="pagination">
          <div className="pagination__count">Всего: {zipUnloadingsCount}</div>
          <div className="pagination__str">
            <span>Отображать строки</span>
            <LimitSelect limit={actualLimit} propsChange={handleChangeLimits} />
          </div>
          <Pagination
            setCurrentPage={setCurrentPage}
            setStartPage={setStartPage}
            handleChange={handleChange}
            count={zipUnloadingsCount}
            currentPage={currentPage}
            pageName="userReports"
            startPage={startPage}
            limit={actualLimit}
          />
        </div>
      </div>
    </>
  );
};

export default TableUnloadingZip;
